import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"

import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import { getQcCallResults, getQcRecordingUrl, reviewQcCallResult } from "../../../features/qcCall/qcCall"

const C = {
  green: '#16a34a',
  greenBg: 'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.45)',
  red: '#ef4444',
  redText: '#f87171',
  redBg: 'rgba(239,68,68,0.12)',
  redBorder: 'rgba(239,68,68,0.5)',
  amber: '#f59e0b',
  amberBg: 'rgba(245,158,11,0.14)',
  blue: '#6366f1',
  card: 'rgba(255,255,255,0.035)',
  cardBorder: 'rgba(255,255,255,0.09)',
  divider: 'rgba(255,255,255,0.07)',
}

const REVIEW_BADGE = {
  no_review_needed: { label: 'No review needed', color: C.green, bg: C.greenBg },
  auto_failed: { label: 'Failed', color: C.redText, bg: C.redBg },
  needs_review: { label: 'Needs review', color: C.amber, bg: C.amberBg },
  approved: { label: 'Passed', color: C.green, bg: C.greenBg },
  rejected: { label: 'Failed', color: C.redText, bg: C.redBg },
}

const formatDate = (value) => value ? new Date(value).toLocaleString() : '—'

const Chevron = ({ open }) => (
  <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
)

const Pill = ({ label, color, bg }) => (
  <span className="px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap" style={{ color, backgroundColor: bg }}>{label}</span>
)

const Card = ({ children, style }) => (
  <div className="rounded-2xl overflow-hidden shrink-0" style={{ backgroundColor: C.card, border: `1px solid ${C.cardBorder}`, ...style }}>{children}</div>
)

const SectionHead = ({ title, right }) => (
  <div className="flex items-center justify-between px-4 py-2.5" style={{ borderBottom: `1px solid ${C.divider}` }}>
    <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-text-secondary">{title}</span>
    {right}
  </div>
)

const IdentityCard = ({ label, spoken, onFile, state }) => {
  const match = state === 'match'
  const mismatch = state === 'mismatch'
  const pill = mismatch
    ? <Pill label="Mismatch" color={C.redText} bg={C.redBg} />
    : match
      ? <Pill label="Match" color={C.green} bg={C.greenBg} />
      : <Pill label="Not captured" color="#9ca3af" bg="rgba(255,255,255,0.06)" />
  return (
    <div className="rounded-xl p-3" style={{ backgroundColor: mismatch ? C.redBg : 'rgba(255,255,255,0.02)', border: `1px solid ${mismatch ? C.redBorder : C.divider}` }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-text-secondary">{label}</span>
        {pill}
      </div>
      <div className="flex items-stretch gap-2">
        <div className="flex-1 rounded-lg px-3 py-2" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
          <div className="text-[10px] font-bold uppercase tracking-wide mb-0.5" style={{ color: C.blue }}>Spoken on call</div>
          <div className="text-sm font-semibold" style={{ color: mismatch ? C.redText : 'var(--color-text-primary)' }}>{spoken || '—'}</div>
        </div>
        <div className="flex items-center text-text-secondary/50">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </div>
        <div className="flex-1 rounded-lg px-3 py-2" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
          <div className="text-[10px] font-bold uppercase tracking-wide mb-0.5 text-text-secondary">On file</div>
          <div className="text-sm font-semibold text-text-primary">{onFile || '—'}</div>
        </div>
      </div>
    </div>
  )
}

const QcCallReviewDrawer = ({ open, userId, onClose }) => {
  const dispatch = useDispatch()
  const [results, setResults] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [recording, setRecording] = useState({ loading: false, available: false, url: null })
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [showQuestions, setShowQuestions] = useState(false)
  const [showTranscript, setShowTranscript] = useState(false)

  const selected = results.find((r) => r._id === selectedId) || null

  useEffect(() => {
    if (!open) return
    const handleEsc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleEsc)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  const loadRecording = async (id) => {
    setRecording({ loading: true, available: false, url: null })
    try {
      const res = await dispatch(getQcRecordingUrl(id))
      const data = res.payload && res.payload.data
      if (data && data.available) {
        setRecording({ loading: false, available: true, url: data.url })
      } else {
        setRecording({ loading: false, available: false, url: null })
      }
    } catch (e) {
      setRecording({ loading: false, available: false, url: null })
    }
  }

  const selectAttempt = (id) => {
    setSelectedId(id)
    setNote('')
    setShowQuestions(false)
    setShowTranscript(false)
    loadRecording(id)
  }

  const fetchResults = async (keepSelection) => {
    const res = await dispatch(getQcCallResults(userId))
    const list = (res.payload && res.payload.data) || []
    setResults(list)
    if (!keepSelection && list.length > 0) {
      selectAttempt(list[0]._id)
    }
  }

  useEffect(() => {
    if (open) fetchResults(false)
  }, [open])

  const handleVerdict = async (decision) => {
    if (!selected) return
    setBusy(true)
    try {
      const res = await dispatch(reviewQcCallResult({ id: selected._id, decision, note }))
      if (res.payload && res.payload.data) {
        showSnackBar('success', `QC Call ${decision === 'approved' ? 'passed' : 'failed'}`)
        await fetchResults(true)
      } else {
        showSnackBar('error', 'Failed to submit verdict')
      }
    } catch (e) {
      showSnackBar('error', 'Failed to submit verdict')
    }
    setBusy(false)
  }

  if (!open) return null

  const name = selected?.identityMatch?.name || {}
  const ssn = selected?.identityMatch?.ssn || {}
  const hasMismatch = name.state === 'mismatch' || ssn.state === 'mismatch'
  const allMatch = name.state === 'match' && ssn.state === 'match'
  const questions = selected?.questions || []
  const yesCount = questions.filter((q) => String(q.answer).toLowerCase() === 'yes').length
  const reviewed = selected?.reviewStatus === 'approved' || selected?.reviewStatus === 'rejected'
  const needsReview = selected?.reviewStatus === 'needs_review'
  const autoFailed = selected?.reviewStatus === 'auto_failed'

  return (
    <div className="fixed inset-0 z-50 flex justify-end font-fontFamily" role="dialog" aria-modal="true" aria-label="QC Call review">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-[760px] max-w-[96vw] h-full bg-paper shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: `1px solid ${C.divider}` }}>
          <div>
            <div className="font-bold text-xl leading-tight text-text-primary">QC Call review</div>
            {selected && <div className="text-xs text-text-secondary mt-1 capitalize">{selected.verificationStatus || 'completed'} · {formatDate(selected.callTimestamp || selected.createdAt)}</div>}
          </div>
          <button onClick={onClose} aria-label="Close" className="p-2 rounded-xl hover:bg-white/5 transition-colors">
            <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {results.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-text-secondary gap-3">
            <svg className="w-12 h-12 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" /></svg>
            <span className="text-sm">No QC calls for this contact.</span>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto px-6 py-4 flex flex-col gap-3.5">
            {results.length > 1 && (
              <div className="flex flex-wrap gap-2 shrink-0">
                {results.map((r) => {
                  const review = REVIEW_BADGE[r.reviewStatus] || REVIEW_BADGE.no_review_needed
                  const active = r._id === selectedId
                  return (
                    <button key={r._id} onClick={() => selectAttempt(r._id)}
                      className="px-3 py-1.5 rounded-full text-xs transition-colors"
                      style={active
                        ? { color: '#fff', backgroundColor: C.blue }
                        : { color: 'var(--color-text-secondary)', border: `1px solid ${C.cardBorder}` }}>
                      {formatDate(r.callTimestamp || r.createdAt)} · {review.label}
                    </button>
                  )
                })}
              </div>
            )}

            {selected && (
              <>
                <Card style={{ borderColor: hasMismatch ? C.redBorder : allMatch ? C.greenBorder : C.cardBorder }}>
                  <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: `1px solid ${C.divider}`, backgroundColor: hasMismatch ? C.redBg : allMatch ? C.greenBg : 'transparent' }}>
                    {hasMismatch ? (
                      <>
                        <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(239,68,68,0.2)' }}>
                          <span className="text-lg" style={{ color: C.red }}>⚠</span>
                        </div>
                        <div>
                          <p className="text-sm font-bold" style={{ color: C.redText }}>Identity does not match on file</p>
                          <p className="text-[11px]" style={{ color: 'rgba(248,113,113,0.85)' }}>A mismatch never auto-fails — listen before deciding.</p>
                        </div>
                      </>
                    ) : allMatch ? (
                      <>
                        <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(22,163,74,0.22)' }}>
                          <svg className="w-4 h-4" fill="none" stroke={C.green} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                        </div>
                        <p className="text-sm font-bold" style={{ color: C.green }}>Identity verified</p>
                      </>
                    ) : (
                      <p className="text-sm text-text-secondary">Identity partially captured — review before deciding.</p>
                    )}
                  </div>
                  <div className="px-4 py-3 flex flex-col gap-2.5">
                    <IdentityCard label="Full name" spoken={name.spoken} onFile={name.onFile} state={name.state} />
                    <IdentityCard label="SSN — last 4" spoken={ssn.spoken} onFile={ssn.onFile} state={ssn.state} />
                  </div>
                </Card>

                <Card>
                  <SectionHead title="Recording" />
                  <div className="px-4 py-3">
                    {recording.loading && <p className="text-sm text-text-secondary">Loading recording…</p>}
                    {!recording.loading && recording.available &&
                      <audio controls src={recording.url} className="w-full h-11"
                        onError={() => loadRecording(selected._id)} />}
                    {!recording.loading && !recording.available &&
                      <p className="text-sm text-text-secondary">No recording available for this call.</p>}
                  </div>
                </Card>

                <Card>
                  <SectionHead
                    title="Verification questions"
                    right={
                      <button onClick={() => setShowQuestions((v) => !v)} className="flex items-center gap-2 text-xs hover:opacity-80">
                        <span className="font-bold" style={{ color: yesCount === questions.length ? C.green : C.amber }}>{yesCount}/{questions.length} answered</span>
                        <Chevron open={showQuestions} />
                      </button>
                    }
                  />
                  {showQuestions && (
                    <div className="px-4 py-2">
                      {questions.map((q) => {
                        const yes = String(q.answer).toLowerCase() === 'yes'
                        return (
                          <div key={q.id} className="flex items-center justify-between py-2.5" style={{ borderBottom: `1px solid ${C.divider}` }}>
                            <span className="text-sm text-text-primary pr-4">{q.label}</span>
                            <Pill label={q.answer} color={yes ? C.green : C.amber} bg={yes ? C.greenBg : C.amberBg} />
                          </div>
                        )
                      })}
                    </div>
                  )}
                </Card>

                {selected.transcript && (
                  <Card>
                    <SectionHead
                      title="Transcript"
                      right={
                        <button onClick={() => setShowTranscript((v) => !v)} className="flex items-center gap-2 text-xs text-text-secondary hover:opacity-80">
                          <span className="font-semibold">{showTranscript ? 'Hide' : 'Show'}</span>
                          <Chevron open={showTranscript} />
                        </button>
                      }
                    />
                    {showTranscript && (
                      <div className="px-4 py-4">
                        <pre className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed max-h-56 overflow-auto font-fontFamily">{selected.transcript}</pre>
                      </div>
                    )}
                  </Card>
                )}
              </>
            )}
          </div>
        )}

        {selected && (
          <div className="shrink-0 px-6 py-4 bg-paper" style={{ borderTop: `1px solid ${C.divider}` }}>
            {reviewed ? (
              <div className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ backgroundColor: C.card, border: `1px solid ${C.cardBorder}` }}>
                <Pill label={REVIEW_BADGE[selected.reviewStatus].label} color={REVIEW_BADGE[selected.reviewStatus].color} bg={REVIEW_BADGE[selected.reviewStatus].bg} />
                <div className="text-xs text-text-secondary">
                  by {selected.reviewedBy || '—'} · {formatDate(selected.reviewedAt)}
                  {selected.reviewNote && <span className="block text-text-primary mt-1">“{selected.reviewNote}”</span>}
                </div>
              </div>
            ) : autoFailed ? (
              <div className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ backgroundColor: C.redBg, border: `1px solid ${C.redBorder}` }}>
                <svg className="w-5 h-5 shrink-0" fill="none" stroke={C.red} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                <div className="text-xs">
                  <span className="font-bold" style={{ color: C.redText }}>Automatically failed</span>
                  <span className="block text-text-secondary mt-0.5">A verification question was declined — the call did not pass.</span>
                </div>
              </div>
            ) : !needsReview ? (
              <div className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ backgroundColor: C.greenBg, border: `1px solid ${C.greenBorder}` }}>
                <svg className="w-5 h-5 shrink-0" fill="none" stroke={C.green} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                <div className="text-xs">
                  <span className="font-bold" style={{ color: C.green }}>No manual review needed</span>
                  <span className="block text-text-secondary mt-0.5">Identity matched automatically — nothing to approve or reject.</span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                  placeholder="Add a reviewer note (saved with the verdict and added to contact notes)…"
                  className="w-full rounded-xl px-3.5 py-3 text-sm bg-paper border border-divider text-text-primary placeholder:text-text-secondary focus:outline-none focus:border-primary-main"
                  style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: `1px solid ${C.cardBorder}` }} />
                <div className="flex items-center gap-3">
                  <button disabled={busy} onClick={() => handleVerdict('approved')}
                    className="flex-1 px-4 py-3 rounded-xl text-white text-sm font-bold transition-all disabled:opacity-50 hover:brightness-110 flex items-center justify-center gap-2"
                    style={{ backgroundColor: C.green, boxShadow: '0 4px 14px rgba(22,163,74,0.3)' }}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                    Pass
                  </button>
                  <button disabled={busy} onClick={() => handleVerdict('rejected')}
                    className="flex-1 px-4 py-3 rounded-xl text-white text-sm font-bold transition-all disabled:opacity-50 hover:brightness-110 flex items-center justify-center gap-2"
                    style={{ backgroundColor: C.red, boxShadow: '0 4px 14px rgba(239,68,68,0.3)' }}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                    Fail
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default QcCallReviewDrawer
